import { useTranslation } from "react-i18next";
import cn from "classnames";
import { ArrowRight2 } from "iconsax-react";
import { Button } from "@/shared/ui";
import type { BudgetSubcategory } from "../../../types";
import { formatCurrencyNoDecimals, removeSectionNumber, clampProgressWidth, SUBCATEGORY_ICONS } from "../../../utils";
import { getSubcategoryColorClasses } from "../../../utils/consts";
import { getLocalizedText } from "../../../utils/localization";

interface Props {
  subcategories: BudgetSubcategory[];
  onSubcategoryClick: (subcategory: BudgetSubcategory) => void;
  hidePlanned?: boolean;
}

const SLOT_COUNT = 4;

function PlaceholderCard() {
  return (
    <div aria-hidden="true" className="p-4 radius-sm surface-base-fill h-full invisible">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-5 h-5 rounded-md! shrink-0" />
          <div className="text-label-sm">Placeholder</div>
        </div>
        <div className="w-5 h-5 shrink-0" />
      </div>
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <span className="text-display-2xs">0</span>
        <span className="text-label-sm">0%</span>
      </div>
      <div className="flex gap-0.5">
        <div className="h-1.5 radius-2xs w-1/2" />
        <div className="h-1.5 radius-2xs flex-1" />
      </div>
    </div>
  );
}

export default function CategoryList({ subcategories, onSubcategoryClick, hidePlanned = false }: Props) {
  const { i18n } = useTranslation("FinancePage");
  const locale = i18n.language || "ru";
  const placeholderCount = Math.max(0, SLOT_COUNT - subcategories.length);

  return (
    <div className="grid grid-cols-1 gap-2 h-full">
      {subcategories.map((sub, index) => {
        const { fill } = getSubcategoryColorClasses(sub.id, index);
        const Icon = SUBCATEGORY_ICONS[sub.id];

        return (
          <div key={sub.id} className="p-4 radius-sm surface-base-fill h-full">
            <div className="flex items-center justify-between mb-4 gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className={cn("w-5 h-5 rounded-md! flex shrink-0 items-center justify-center radial-glow", fill)}>
                  {Icon && <Icon size={12} color="currentColor" className="text-white" />}
                </div>
                <span className="text-label-sm content-action-neutral truncate">
                  {removeSectionNumber(getLocalizedText(sub.name, locale))}
                </span>
              </div>
              <Button
                type="button"
                variant="secondary"
                isIconButton
                onClick={() => onSubcategoryClick(sub)}
                className="w-5! h-5! p-0! rounded-sm! flex items-center justify-center border surface-base-stroke content-base-primary cursor-pointer">
                <ArrowRight2 size={12} color="currentColor" className="currentColor" />
              </Button>
            </div>

            <div className="flex items-baseline justify-between mb-2 gap-3">
              <div className="flex items-end gap-0.5">
                <span className="text-display-2xs content-base-primary">
                  {formatCurrencyNoDecimals(sub.total, false)}
                </span>
                <span className="text-heading-md content-base-secondary">₸</span>
              </div>
              {!hidePlanned && (
                <span className="text-label-sm content-base-low">{sub.execution_percentage.toFixed(0)}%</span>
              )}
            </div>

            {!hidePlanned && (
              <div className="flex gap-0.5">
                <div
                  className={cn("h-1.5 radius-2xs", fill)}
                  style={{ width: `${clampProgressWidth(sub.execution_percentage)}%` }}
                />
                <div className="h-1.5 radius-2xs surface-component-stroke-fill flex-1" />
              </div>
            )}
          </div>
        );
      })}

      {Array.from({ length: placeholderCount }).map((_, i) => (
        <PlaceholderCard key={`placeholder-${i}`} />
      ))}
    </div>
  );
}
